<script setup lang="ts">
import { getCsrfToken } from '@/helpers/auth';
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload';
import Flow, { type FlowFile } from '@flowjs/flow.js';
import { onMounted, ref, type Ref } from 'vue';
import { apiUrl } from '@/helpers/api';
import ProgressBar from 'primevue/progressbar';
import { mimeToIconCls } from '@/helpers/tools';

const props = defineProps<{
    additionalParams?: Record<string, any>;
}>();

type UploadEntry = {
    flowFile: FlowFile | null;
    document: Document | null;
    uploadProgress: number;
    uploadStarted: boolean;
    error?: string | null;
    errorFile?: File | null;
};

const emit = defineEmits(['file-selected', 'file-uploaded']);

const uploadEntries: Ref<UploadEntry[]> = ref([]);
function onFileSelected(event: FileUploadSelectEvent) {
    event.files.forEach((file: File) => {
        flow.addFile(file);
    });
    emit('file-selected', event.files);
}

const progress = ref(0);
const uploading = ref(false);
const disableButtons = ref(false);

defineExpose({ uploadFiles });

function uploadFiles(): Promise<FlowFile | null> {
    disableButtons.value = true;
    return new Promise((resolve, reject) => {
        if (flow.files.length === 0) {
            disableButtons.value = false;
            return resolve(null);
        }
        progress.value = 0;
        uploading.value = true;
        flow.opts.query = {
            ...props.additionalParams,
        };

        // remove previous listeners, if set:
        flow.off('complete');
        flow.off('error');
        flow.off('progress');
        flow.off('fileProgress');
        flow.off('fileSuccess');
        // error.value = null;

        const fn = (file: FlowFile, message: string) => {
            flow.off('fileSuccess', fn as any);
            // on upload end, we emit the final Document models
            emit('file-uploaded', message);
            reset();
            disableButtons.value = false;
            resolve(file);
        };

        flow.on('fileSuccess', fn);
        flow.on('error', (message, file) => {
            const res = JSON.parse(message);
            disableButtons.value = false;
            reset();
            reject(res);
        });

        flow.on('progress', () => {
            progress.value = Math.round((flow.sizeUploaded() / flow.getSize()) * 100);
        });

        // update the individual upload entrie's progress, to update the progess bar:
        flow.on('fileProgress', (file: FlowFile) => {
            const uploadEntry = uploadEntries.value.find(
                (entry) => entry.flowFile?.uniqueIdentifier === file.uniqueIdentifier,
            );
            if (uploadEntry) {
                uploadEntry.uploadProgress = Math.round((file.sizeUploaded() / file.size) * 100);
            }
        });
        flow.on('fileSuccess', (file: FlowFile, message: string) => {
            const response = JSON.parse(message);
            if (response?.documents?.length > 0) {
                // const docResponse = response.documents[0];
                const uploadEntry = uploadEntries.value.find(
                    (entry) => entry.flowFile?.uniqueIdentifier === file.uniqueIdentifier,
                );
                if (uploadEntry) {
                    // uploadEntry.document = createModel(Document, docResponse);
                    // uploadEntry.document.setPhantom(false);
                    uploadEntry.flowFile = null;
                }
            }
        });

        uploadEntries.value.forEach((entry) => {
            entry.uploadStarted = true;
        });

        // start the upload queue!
        flow.upload();
    });
}

function reset() {
    flow.cancel();
    uploading.value = false;
    progress.value = 0;
    disableButtons.value = false;
}

let flow: Flow;
function initFlow() {
    flow = new Flow({
        singleFile: true,
        simultaneousUploads: 1,
        target: apiUrl('/Document/uploadFile'),
        testChunks: false,
        chunkSize: 1024 * 1024,
        allowDuplicateUploads: true,
        headers: {
            'X-CSRF-Token': getCsrfToken(),
        },
    });
}

onMounted(() => {
    initFlow();
});
</script>

<template>
    <FileUpload
        customUpload
        :previewWidth="0"
        ref="fileupload"
        :showUploadButton="false"
        :showCancelButton="!disableButtons"
        :chooseLabel="$t('datei_waehlen')"
        :multiple="false"
        :fileLimit="1"
        name="file"
        @select="onFileSelected"
    >
        <template #empty>
            <div class="h-5rem flex align-items-center flex-column justify-content-center">
                Ziehen Sie die Dateien per Drag & Drop hierher, um sie hochzuladen.
            </div>
        </template>
        <template #content="{ files, uploadedFiles }">
            <div v-for="file in files" :key="file.name + file.type + file.size">
                <div class="flex flex-col sm:flex-row sm:items-center p-6 gap-4 align-items-center">
                    <div class="md:w-40 relative">
                        <i :class="mimeToIconCls(file.type) + ' fa-2xl'"></i>
                    </div>
                    <span class="font-medium text-surface-500 dark:text-surface-400 text-sm">{{ file.name }}</span>
                </div>
                <ProgressBar class="h-1rem" :showValue="false" :value="progress"></ProgressBar>
            </div>
            <div v-for="file in uploadedFiles" :key="file.name + file.type + file.size">
                <div class="flex flex-col sm:flex-row sm:items-center p-6 gap-4 align-items-center">
                    <div class="md:w-40 relative">
                        <i :class="mimeToIconCls(file.type) + ' fa-2xl'"></i>
                    </div>
                    <span class="font-medium text-surface-500 dark:text-surface-400 text-sm">{{ file.name }}</span>
                </div>
                <ProgressBar class="h-1rem" :value="100"></ProgressBar>
            </div>
        </template>
    </FileUpload>
</template>
